import { Center, Loader, TextInput } from "@mantine/core";
import { FiArrowRight, FiChevronRight, FiSearch } from "react-icons/fi";
import { MdApartment } from "react-icons/md";
import { useGetMcbimBuildingStoriesQuery } from "../../../openapi";
import type { GetMcbimBuildingStoriesApiResponse } from "../../../openapi";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { selectBuildingStoriesGroupedByBuilding } from "../designSelector";
import {
  setBuildingStoryListRedux,
  setSelectedBuildingInRedux,
  setFloorAndJobInRedux,
} from "../designSlice";

export const BuildingStoryList: React.FC = () => {
  const dispatch = useAppDispatch();
  const projectNo = useAppSelector((state) => state.design.projectNo);
  const selectedBuilding = useAppSelector((state) => state.design.selectedBuilding);
  const listState = useAppSelector((state) => state.design.buildingStoryList);
  const grouped = useAppSelector(selectBuildingStoriesGroupedByBuilding);
  const { isLoading, isError, refetch } = useGetMcbimBuildingStoriesQuery(
    { projectNo: projectNo! },
    { skip: !projectNo }
  );
  const activeGroup = grouped.find((group) => group.building === selectedBuilding) ?? grouped[0];

  const openFloor = (story: GetMcbimBuildingStoriesApiResponse[number]) => {
    dispatch(setSelectedBuildingInRedux(activeGroup.building));
    dispatch(
      setFloorAndJobInRedux({
        building: story.building,
        floorName: story.name,
        floorGlobalId: story.globalId,
        jobId: story.job.jobId,
        elevation: story.elevation,
      })
    );
  };

  if (!projectNo) return <p className="floor-picker-message">Velg et prosjekt for å se etasjer.</p>;
  if (isLoading)
    return (
      <Center flex={1}>
        <Loader size="sm" aria-label="Henter etasjer" />
      </Center>
    );
  if (isError)
    return (
      <div className="floor-picker-message" role="alert">
        <p>Klarte ikke å hente etasjer.</p>
        <button className="floor-picker-text-button" onClick={() => refetch()}>
          Prøv igjen
        </button>
      </div>
    );

  return (
    <>
      <div className="floor-picker-search">
        <TextInput
          aria-label="Søk etter bygning eller etasje"
          placeholder="Søk etter bygning eller etasje…"
          leftSection={<FiSearch size={16} />}
          size="sm"
          value={listState.search}
          onChange={(event) =>
            dispatch(setBuildingStoryListRedux({ ...listState, search: event.currentTarget.value }))
          }
        />
      </div>
      {grouped.length === 0 ? (
        <div className="floor-picker-message" role="status">
          <p>
            {listState.search.trim()
              ? "Ingen bygninger eller etasjer matcher søket."
              : "Ingen etasjer funnet i dette prosjektet."}
          </p>
          {listState.search.trim() && (
            <button
              className="floor-picker-text-button"
              onClick={() => dispatch(setBuildingStoryListRedux({ ...listState, search: "" }))}
            >
              Tøm søk
            </button>
          )}
        </div>
      ) : (
        <div className="floor-picker-columns">
          <section
            className="floor-picker-buildings"
            aria-labelledby="floor-picker-buildings-heading"
          >
            <h2 id="floor-picker-buildings-heading">
              Bygninger <span>{grouped.length}</span>
            </h2>
            <div className="floor-picker-scroll">
              {grouped.map((group) => (
                <button
                  key={group.building}
                  className="floor-picker-building"
                  aria-pressed={group.building === activeGroup.building}
                  onClick={() => dispatch(setSelectedBuildingInRedux(group.building))}
                >
                  <MdApartment size={18} aria-hidden="true" />
                  <span className="floor-picker-name">
                    {group.building === "Unknown" ? "Uten bygning" : group.building}
                  </span>
                  <span className="floor-picker-count">
                    {group.floors.length} {group.floors.length === 1 ? "etasje" : "etasjer"}
                  </span>
                  <FiChevronRight size={14} aria-hidden="true" />
                </button>
              ))}
            </div>
          </section>
          <section className="floor-picker-floors" aria-labelledby="floor-picker-floors-heading">
            <h2 id="floor-picker-floors-heading">
              {activeGroup.building === "Unknown"
                ? "Etasjer uten bygning"
                : `Etasjer i ${activeGroup.building}`}{" "}
              <span>{activeGroup.floors.length}</span>
            </h2>
            <div className="floor-picker-scroll">
              {activeGroup.floors.map((story) => (
                <button
                  key={`${story.globalId}_${story.job.jobId}`}
                  className="floor-picker-floor"
                  onClick={() => openFloor(story)}
                >
                  <span className="floor-picker-name">{story.name}</span>
                  <span className="floor-picker-count">{story.spaceCount} rom</span>
                  <FiArrowRight size={16} aria-hidden="true" />
                </button>
              ))}
            </div>
          </section>
        </div>
      )}
      <footer className="floor-picker-footer">
        Du kan bytte etasje fra verktøylinjen når som helst.
      </footer>
    </>
  );
};
